/**
 * МАКЕТ «Торговий графік: модель пропонує — троє погоджують».
 *
 * Каркас для обговорення за концепцією від 13.10 (розділи 8–11): модель будує
 * графік із трьох складових (прогноз генерації, прогноз ціни, технічний стан),
 * пояснює кожну годину, а три ролі ставлять свої галочки, після чого графік іде
 * на MMS. Дані демонстраційні — див. mockData.ts; жодного запиту до API сторінка
 * не робить, нічого не зберігає.
 *
 * Оформлення — за прийнятим стандартом кабінету, без винятків:
 *  · акцент НЕЙТРАЛЬНИЙ (--text / --line-strong), брендовий червоний не вживаємо
 *    як «просто важливе» — за догмою він лишається за реальною помилкою;
 *  · внутрішня навігація — SegmentedTabs, дії — ActionButton;
 *  · графік — useChartTheme + ChartLegend (палітра серій без червоного).
 *
 * Маршрут: /dispatch/schedule-draft
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import ActionButton from '@/components/base/ActionButton';
import ChartLegend from '@/components/base/ChartLegend';
import { useChartTheme } from '@/components/base/chartTheme';
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea,
} from 'recharts';
import {
  Check, Clock, Gauge, LineChart, Wrench, AlertTriangle, Send,
  Sparkles, ArrowRight, Info, Zap,
} from 'lucide-react';
import { useScheduleDraftUi } from '@/hooks/useScheduleDraftUi';
import {
  HOURS, PRICE_RANK, DISCHARGE_WINDOW, CHARGE_WINDOW, TOTALS,
  TECH_STATE, TECH_EVENTS, ACCURACY_7D, ROLES, ROLE_KEYS, type RoleKey,
} from './mockData';

const UAH = (v: number) => v.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { maximumFractionDigits: 0 });
const MWH = (v: number) => v.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const hLabel = (h: number) => `${String(h - 1).padStart(2, '0')}:00`;
/** 2026-10-15 → «15.10» */
const dmLabel = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

const ROLE_ICON: Record<RoleKey, typeof Gauge> = {
  commercial: Gauge,
  trader: LineChart,
  technical: Wrench,
};

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="tile p-3.5">
      <div className="text-[10.5px] font-bold uppercase mb-1.5" style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div className="font-display text-[19px] font-semibold leading-none" style={{ color: tone ?? 'var(--text)' }}>
        {value}
      </div>
      {sub && <div className="text-[11px] mt-1.5" style={{ color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-bold uppercase mb-2.5" style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>
      {children}
    </div>
  );
}

export default function ScheduleDraftMockPage() {
  const { t } = useTranslation();
  const ct = useChartTheme();
  const sd = useScheduleDraftUi();   // доба й горизонт живуть у топбарі
  const [role, setRole] = useState<RoleKey>('commercial');
  const [ask, setAsk] = useState('');
  const [showDiff, setShowDiff] = useState(false);
  const [signed, setSigned] = useState<Record<RoleKey, boolean>>({
    commercial: ROLES.commercial.signed,
    trader: ROLES.trader.signed,
    technical: ROLES.technical.signed,
  });

  const signedCount = ROLE_KEYS.filter((k) => signed[k]).length;

  const chartData = useMemo(() => HOURS.map((r) => ({
    ...r,
    label: hLabel(r.h),
    band: [r.genP10, r.genP90] as [number, number],
  })), []);

  const rankedHours = useMemo(
    () => [...HOURS].sort((a, b) => PRICE_RANK[a.h] - PRICE_RANK[b.h]),
    [],
  );

  return (
    <DashboardLayout breadcrumb={[t('ak_sd_dispatch_room'), t('ak_sd_trading_schedule_mockup')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* ── Шапка: документ, доба, стан погодження ── */}
          <div className="tile p-4 sm:p-5 mb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded"
                    style={{
                      background: 'color-mix(in srgb, var(--text) 8%, transparent)',
                      color: 'var(--text-muted)', letterSpacing: '0.06em',
                    }}>
                    {t('ak_sd_mockup')}
                  </span>
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {t('ak_sd_demo_data_nothing_is_sent')}
                  </span>
                </div>
                <h1 className="font-display text-[20px] font-semibold leading-tight" style={{ color: 'var(--text)' }}>
                  {t('ak_sd_trading_schedule_for_elios_luchky_sola', { d: dmLabel(sd.date) })}
                </h1>
                <div className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {t('ak_sd_draft_prepared_by_the_model_at_08_40')}{' '}
                  {sd.horizon === 'd2'
                    ? t('ak_sd_horizon_d_2_accuracy_noticeably_lower')
                    : sd.horizon === 'd1' ? t('ak_sd_horizon_d_1') : t('ak_sd_day_outside_the_submission_horizon')}
                </div>
              </div>

              {/* Доба подачі — у топбарі, як на «Прогнозі» й «Цінах». */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-right">
                  <div className="text-[10.5px] font-bold uppercase" style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>
                    {t('ak_sd_until_mms_submission')}
                  </div>
                  <div className="font-display text-[15px] font-semibold flex items-center gap-1.5 justify-end"
                    style={{ color: signedCount === 3 ? ct.series.ok : 'var(--text)' }}>
                    <Clock size={14} strokeWidth={2} style={{ color: 'var(--text-dim)' }} />
                    {t('ak_sd_10_30_1_h_18_min')}
                  </div>
                </div>
              </div>
            </div>

            {/* Три галочки — головний елемент екрана */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-4">
              {ROLE_KEYS.map((k) => {
                const r = ROLES[k];
                const on = signed[k];
                const Icon = ROLE_ICON[k];
                return (
                  <button
                    key={k}
                    onClick={() => setSigned((s) => ({ ...s, [k]: !s[k] }))}
                    className="flex items-center gap-2.5 p-3 rounded-xl text-left transition-colors"
                    style={{
                      background: on
                        ? `color-mix(in srgb, ${ct.series.ok} 10%, transparent)`
                        : 'var(--surface-2)',
                      border: `1px solid ${on ? `color-mix(in srgb, ${ct.series.ok} 45%, transparent)` : 'var(--line-strong)'}`,
                    }}
                  >
                    <div className="flex items-center justify-center rounded-full flex-shrink-0"
                      style={{
                        width: 26, height: 26,
                        background: on ? ct.series.ok : 'transparent',
                        border: on ? 'none' : '1.5px solid var(--line-strong)',
                      }}>
                      {on
                        ? <Check size={15} strokeWidth={3} color="#fff" />
                        : <Icon size={14} strokeWidth={1.9} style={{ color: 'var(--text-dim)' }} />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold" style={{ color: 'var(--text)' }}>{r.title}</div>
                      <div className="text-[10.5px] truncate" style={{ color: 'var(--text-muted)' }}>
                        {on ? t('ak_sd_approved_by', { p: r.person, at: r.at ?? t('ak_sd_just_now') }) : t('ak_sd_waiting', { p: r.person })}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {signedCount < 3 ? (
              <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg text-[11.5px]"
                style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}>
                <AlertTriangle size={14} strokeWidth={2} style={{ color: ct.series.warn, flexShrink: 0, marginTop: 1 }} />
                <span>
                  {t('ak_sd_if_at')} <b style={{ color: 'var(--text)' }}>10:00</b> {t('ak_sd_not_everyone_has_approved_the_system_w')}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-3 p-2.5 rounded-lg text-[11.5px]"
                style={{ background: `color-mix(in srgb, ${ct.series.ok} 10%, transparent)`, color: 'var(--text)' }}>
                <Send size={14} strokeWidth={2} style={{ color: ct.series.ok, flexShrink: 0 }} />
                <span>{t('ak_sd_all_three_approvals_are_in_the_schedul')}</span>
              </div>
            )}
          </div>

          {/* ── Підсумок доби ── */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 mb-3">
            <Kpi label={t('ak_sd_generation_forecast')} value={`${MWH(TOTALS.genMwh)} ${t('ak_sd_mwh')}`} sub={t('ak_sd_median_p50')} />
            <Kpi label={t('ak_sd_delivery_schedule')} value={`${MWH(TOTALS.planMwh)} ${t('ak_sd_mwh')}`} sub={t('ak_sd_this_is_what_we_submit_to_mms')} />
            <Kpi label={t('ak_sd_bess_charge_discharge')} value={`${MWH(TOTALS.chargeMwh)} / ${MWH(TOTALS.dischargeMwh)}`} sub={t('ak_sd_cycle_efficiency_0_87')} />
            <Kpi label={t('ak_sd_expected_revenue')} value={`${UAH(TOTALS.revenueUah / 1000)} ${t('ak_sd_k_uah')}`} sub={t('ak_sd_schedule_price_forecast_0_97')} />
            <Kpi label={t('ak_sd_technical_state')} value={t('ak_sd_of', { a: TECH_STATE.invertersAlive, b: TECH_STATE.invertersTotal })} sub={t('ak_sd_coefficient_0_97')} tone={ct.series.warn} />
          </div>

          {/* ── Вкладки ролей ── */}
          <div className="mb-3">
            <SegmentedTabs
              items={ROLE_KEYS.map((k) => ({ key: k, label: ROLES[k].title, Icon: ROLE_ICON[k] }))}
              activeKey={role}
              onSelect={(k) => setRole(k as RoleKey)}
            />
          </div>
          <div className="text-[11.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
            {t('ak_sd_each_role_sees_its_own_screen_the_swit')}{' '}{t('ak_sd_area_of_responsibility')} <b style={{ color: 'var(--text)' }}>{ROLES[role].scope}</b>.
          </div>

          {/* ── Графік доби ── */}
          <div className="tile p-4 sm:p-5 mb-3">
            <SectionTitle>{t('ak_sd_day_by_hour')}</SectionTitle>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid {...ct.gridProps} />
                  <XAxis dataKey="label" {...ct.axis} interval={1} />
                  <YAxis yAxisId="mwh" {...ct.axis} width={46} label={ct.axisLabel(t('ak_sd_mwh'))} />
                  <YAxis yAxisId="uah" orientation="right" {...ct.axis} width={54} />
                  <Tooltip
                    {...ct.tooltip}
                    cursor={ct.cursorBar}
                    formatter={(v: any, name: string) => (
                      name === t('ak_sd_price_forecast')
                        ? [`${UAH(Number(v))} ${t('ak_sd_uah_mwh')}`, name]
                        : [`${MWH(Number(v))} ${t('ak_sd_mwh')}`, name]
                    )}
                  />

                  {/* Вікна заряду й розряду — підсвічування, а не серія */}
                  <ReferenceArea yAxisId="mwh" x1={hLabel(DISCHARGE_WINDOW[0])} x2={hLabel(DISCHARGE_WINDOW[1])}
                    fill={ct.series.ok2} fillOpacity={0.10} />
                  <ReferenceArea yAxisId="mwh" x1={hLabel(CHARGE_WINDOW[0])} x2={hLabel(CHARGE_WINDOW[2])}
                    fill={ct.series.s4} fillOpacity={0.14} />

                  <Area yAxisId="mwh" dataKey="band" name={t('ak_sd_corridor_p10_p90')}
                    stroke="none" fill={ct.series.s3} fillOpacity={0.18} isAnimationActive={false} />
                  <Bar yAxisId="mwh" dataKey="plan" name={t('ak_sd_delivery_schedule')}
                    {...ct.bar} fill={ct.series.s2} stroke={ct.series.s2} radius={[3, 3, 0, 0]} />
                  <Line yAxisId="mwh" dataKey="genP50" name={t('ak_sd_generation_forecast_p50')}
                    {...ct.lineThin} stroke={ct.series.s3} />
                  <Line yAxisId="uah" dataKey="priceP50" name={t('ak_sd_price_forecast')}
                    {...ct.lineThin} stroke={ct.series.ok2} strokeDasharray="5 3" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <ChartLegend items={[
              { label: t('ak_sd_delivery_schedule'), color: ct.series.s2, bar: true },
              { label: t('ak_sd_generation_forecast_p50'), color: ct.series.s3 },
              { label: t('ak_sd_corridor_p10_p90'), color: ct.series.s3, bar: true },
              { label: t('ak_sd_dam_price_forecast'), color: ct.series.ok2, dashed: true },
            ]} />
            <div className="flex flex-wrap gap-4 mt-1 text-[11px] justify-center" style={{ color: 'var(--text-muted)' }}>
              <span className="flex items-center gap-1.5">
                <span style={{ width: 11, height: 11, borderRadius: 3, background: ct.series.ok2, opacity: 0.3 }} />
                {t('ak_sd_discharge_window_19_00_21_00')}
              </span>
              <span className="flex items-center gap-1.5">
                <span style={{ width: 11, height: 11, borderRadius: 3, background: ct.series.s4, opacity: 0.4 }} />
                {t('ak_sd_charge_window_11_00_14_00')}
              </span>
            </div>
          </div>

          {/* ── Панель ролі ── */}
          {role === 'commercial' && <CommercialPanel ct={ct} />}
          {role === 'trader' && <TraderPanel ranked={rankedHours} ct={ct} />}
          {role === 'technical' && <TechnicalPanel ct={ct} />}

          {/* ── Рядок живою мовою ── */}
          <div className="tile p-4 sm:p-5 mt-3">
            <SectionTitle>{t('ak_sd_tell_the_model_what_to_change')}</SectionTitle>
            <div className="flex items-center gap-2 p-2.5 rounded-xl"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
              <Sparkles size={16} strokeWidth={1.9} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input
                value={ask}
                onChange={(e) => setAsk(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && ask.trim()) setShowDiff(true); }}
                placeholder={t('ak_sd_write_in_plain_language_move_the_disch')}
                className="flex-1 bg-transparent outline-none text-[12.5px]"
                style={{ color: 'var(--text)' }}
              />
              <ActionButton Icon={ArrowRight} onClick={() => ask.trim() && setShowDiff(true)} compact>
                {t('ak_sd_recalculate')}
              </ActionButton>
            </div>

            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {[
                t('ak_sd_move_the_discharge_to_21_22'),
                t('ak_sd_apply_the_16_mw_limit_from_hour_14'),
                t('ak_sd_do_not_set_zero_at_hour_14_give_the_re'),
                t('ak_sd_show_why_exactly_these_two_discharge_h'),
              ].map((s) => (
                <button key={s} onClick={() => { setAsk(s); setShowDiff(true); }}
                  className="px-2.5 py-1 rounded-lg text-[11px] transition-colors"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text-muted)' }}>
                  {s}
                </button>
              ))}
            </div>

            {showDiff && (
              <div className="mt-3 p-3.5 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--line-strong)' }}>
                <div className="flex items-center gap-2 mb-2.5">
                  <Info size={14} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
                  <span className="text-[12px] font-semibold" style={{ color: 'var(--text)' }}>
                    {t('ak_sd_the_model_proposes_a_change_confirm_or')}
                  </span>
                </div>
                <div className="text-[11.5px] mb-2.5" style={{ color: 'var(--text-muted)' }}>
                  {t('ak_sd_the_agent')} <b style={{ color: 'var(--text)' }}>{t('ak_sd_does_not_draw_numbers_itself')}</b>{t('ak_sd_it_translates_the_request_into_a_param')}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11.5px]">
                  {[
                    [t('ak_sd_discharge_window'), '20:00–21:00', '21:00–22:00'],
                    [t('ak_sd_expected_revenue'), t('ak_sd_uah_1_198k'), t('ak_sd_uah_1_186k')],
                    [t('ak_sd_imbalance_risk'), t('ak_sd_low'), t('ak_sd_medium')],
                  ].map(([k, a, b]) => (
                    <div key={k} className="p-2.5 rounded-lg" style={{ background: 'var(--surface-solid)' }}>
                      <div className="text-[10.5px] uppercase font-bold mb-1" style={{ color: 'var(--text-dim)', letterSpacing: '0.06em' }}>{k}</div>
                      <div style={{ color: 'var(--text-dim)', textDecoration: 'line-through' }}>{a}</div>
                      <div className="font-semibold" style={{ color: 'var(--text)' }}>{b}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <ActionButton Icon={Check} onClick={() => setShowDiff(false)} compact>{t('ak_sd_apply')}</ActionButton>
                  <ActionButton tone="ghost" onClick={() => { setShowDiff(false); setAsk(''); }} compact>{t('ak_sd_cancel')}</ActionButton>
                </div>
                <div className="text-[10.5px] mt-2.5" style={{ color: 'var(--text-dim)' }}>
                  {t('ak_sd_the_request_and_decision_will_be_logge')}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
}

type Ct = ReturnType<typeof useChartTheme>;

/* ───────────────────────── Панель 1: комерційний диспетчер ──────────────── */
function CommercialPanel({ ct }: { ct: Ct }) {
  const { t } = useTranslation();
  const worst = ACCURACY_7D.reduce((a, b) => (b.nmae > a.nmae ? b : a));
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
        <Kpi label={t('ak_sd_accuracy_over_7_days')} value={t('ak_sd_12_0_nmae')} sub={t('ak_sd_target_7')} tone={ct.series.warn} />
        <Kpi label={t('ak_sd_worst_day')} value={`${worst.nmae} %`} sub={worst.d} />
        <Kpi label={t('ak_sd_change_vs_evening')} value="+18 %" sub={t('ak_sd_morning_forecast_is_higher_alarm_at_08')} tone={ct.series.warn} />
        <Kpi label={t('ak_sd_corridor_width')} value="±27 %" sub={t('ak_sd_variable_cloud_cover')} />
      </div>

      <div className="tile p-4 sm:p-5">
        <SectionTitle>{t('ak_sd_hours_worth_a_look')}</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--text-dim)' }}>
                {[t('ak_sd_hour'), t('ak_sd_forecast_p50'), t('ak_sd_corridor'), t('ak_sd_schedule'), t('ak_sd_why')].map((h) => (
                  <th key={h} className="text-left font-bold uppercase text-[10px] pb-2"
                    style={{ letterSpacing: '0.06em', borderBottom: '1px solid var(--line)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HOURS.filter((r) => r.genP50 > 0 || r.discharge > 0).map((r) => (
                <tr key={r.h} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td className="py-1.5 font-semibold" style={{ color: 'var(--text)' }}>{hLabel(r.h)}</td>
                  <td style={{ color: 'var(--text)' }}>{MWH(r.genP50)}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{MWH(r.genP10)} – {MWH(r.genP90)}</td>
                  <td className="font-semibold" style={{ color: r.plan === 0 ? 'var(--text-dim)' : 'var(--text)' }}>
                    {MWH(r.plan)}
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{r.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ───────────────────────── Панель 2: трейдер ────────────────────────────── */
function TraderPanel({ ranked, ct }: { ranked: typeof HOURS; ct: Ct }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
        <Kpi label={t('ak_sd_peak_hour_hit_rate')} value={t('ak_sd_23_of_30_days')} sub={t('ak_sd_for_september')} tone={ct.series.ok} />
        <Kpi label={t('ak_sd_most_expensive_hour')} value="21:00 · 13 200" sub={t('ak_sd_uah_mwh_corridor_20')} />
        <Kpi label={t('ak_sd_cheapest_hour')} value="13:00 · 480" sub={t('ak_sd_we_put_the_charge_here')} />
        <Kpi label={t('ak_sd_daily_spread')} value="27×" sub={t('ak_sd_between_the_most_and_least_expensive')} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="tile p-4 sm:p-5">
          <SectionTitle>{t('ak_sd_hour_ranking_by_price')}</SectionTitle>
          <div className="text-[11.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
            {t('ak_sd_the_model_does_not_promise_an_exact_pr')}
          </div>
          <div className="flex flex-col gap-1">
            {ranked.slice(0, 8).map((r) => {
              const isDis = DISCHARGE_WINDOW.includes(r.h);
              return (
                <div key={r.h} className="flex items-center gap-2.5 p-2 rounded-lg"
                  style={{ background: isDis ? `color-mix(in srgb, ${ct.series.ok2} 12%, transparent)` : 'var(--surface-2)' }}>
                  <span className="font-display text-[12px] font-bold w-6" style={{ color: 'var(--text-dim)' }}>
                    {PRICE_RANK[r.h]}
                  </span>
                  <span className="text-[12.5px] font-semibold w-14" style={{ color: 'var(--text)' }}>{hLabel(r.h)}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--line)' }}>
                    <div style={{
                      width: `${(r.priceP50 / 13200) * 100}%`, height: '100%',
                      background: isDis ? ct.series.ok2 : ct.series.s4,
                    }} />
                  </div>
                  <span className="text-[12px] tabular-nums w-20 text-right" style={{ color: 'var(--text)' }}>{UAH(r.priceP50)}</span>
                  {isDis && <Zap size={13} strokeWidth={2.2} style={{ color: ct.series.ok2 }} />}
                </div>
              );
            })}
          </div>
        </div>

        <div className="tile p-4 sm:p-5">
          <SectionTitle>{t('ak_sd_cheap_hours_zero_or_give')}</SectionTitle>
          <div className="text-[11.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
            {t('ak_sd_vadym_s_question_from_the_12_10_meetin')}
          </div>
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ color: 'var(--text-dim)' }}>
                {[t('ak_sd_hour'), t('ak_sd_dam_price'), t('ak_sd_give'), t('ak_sd_zero'), t('ak_sd_decision')].map((h) => (
                  <th key={h} className="text-left font-bold uppercase text-[10px] pb-2"
                    style={{ letterSpacing: '0.06em', borderBottom: '1px solid var(--line)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { h: 12, price: 620, give: t('ak_sd_1_2k'), zero: '0', pick: t('ak_sd_give_2') },
                { h: 13, price: 480, give: t('ak_sd_0_4k'), zero: '0', pick: t('ak_sd_zero_2') },
                { h: 14, price: 520, give: t('ak_sd_0_3k'), zero: '0', pick: t('ak_sd_give_2') },
              ].map((r) => (
                <tr key={r.h} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td className="py-2 font-semibold" style={{ color: 'var(--text)' }}>{hLabel(r.h)}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{UAH(r.price)}</td>
                  <td style={{ color: r.pick === t('ak_sd_give_2') ? ct.series.ok : 'var(--text-muted)' }}>{r.give}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{r.zero}</td>
                  <td className="font-semibold" style={{ color: r.pick === t('ak_sd_zero_2') ? 'var(--text)' : ct.series.ok }}>{r.pick}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10.5px] mt-3" style={{ color: 'var(--text-dim)' }}>
            {t('ak_sd_a_zero_in_the_schedule_means_the_plant')}
          </div>
        </div>
      </div>
    </>
  );
}

/* ───────────────────────── Панель 3: технічний диспетчер ────────────────── */
function TechnicalPanel({ ct }: { ct: Ct }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
        <Kpi label={t('ak_sd_inverters')} value={t('ak_sd_of', { a: TECH_STATE.invertersAlive, b: TECH_STATE.invertersTotal })} sub={t('ak_sd_2_at_tko_3_under_repair')} tone={ct.series.warn} />
        <Kpi label={t('ak_sd_usable_bess_capacity')} value={`${TECH_STATE.bessUsefulMwh} ${t('ak_sd_mwh')}`} sub={t('ak_sd_charge_discharge_mw', { c: TECH_STATE.bessChargeMw, d: TECH_STATE.bessDischargeMw })} />
        <Kpi label={t('ak_sd_soc_now')} value={`${TECH_STATE.socPct} %`} sub={t('ak_sd_average_across_16_pcs')} />
        <Kpi label={t('ak_sd_tso_restriction')} value={TECH_STATE.osrLimit ?? t('ak_sd_none')} sub={t('ak_sd_delivery_ceiling_mw', { n: TECH_STATE.gridLimitMw })} tone={ct.series.ok} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="tile p-4 sm:p-5">
          <SectionTitle>{t('ak_sd_event_log_basis_for_the_coefficient')}</SectionTitle>
          <div className="flex flex-col gap-2">
            {TECH_EVENTS.map((e) => (
              <div key={e.at} className="flex items-start gap-2.5 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                <div className="flex items-center justify-center rounded-md flex-shrink-0"
                  style={{
                    width: 24, height: 24,
                    background: e.affectsSchedule
                      ? `color-mix(in srgb, ${ct.series.warn} 16%, transparent)`
                      : 'var(--line)',
                  }}>
                  {e.affectsSchedule
                    ? <AlertTriangle size={13} strokeWidth={2.1} style={{ color: ct.series.warn }} />
                    : <Info size={13} strokeWidth={2.1} style={{ color: 'var(--text-dim)' }} />}
                </div>
                <div className="min-w-0">
                  <div className="text-[12px]" style={{ color: 'var(--text)' }}>{e.text}</div>
                  <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
                    {e.at} · {e.kind === 'ремонт' ? t('ak_sd_repair') : e.kind === 'звʼязок' ? t('ak_sd_connectivity') : t('ak_sd_restriction')} · {e.affectsSchedule ? t('ak_sd_affects_the_schedule') : t('ak_sd_does_not_affect_the_schedule')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="tile p-4 sm:p-5">
          <SectionTitle>{t('ak_sd_availability_coefficient')}</SectionTitle>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-display text-[30px] font-semibold leading-none" style={{ color: ct.series.warn }}>0,97</span>
            <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{t('ak_sd_62_64_inverters')}</span>
          </div>
          <div className="flex items-start gap-2 p-2.5 rounded-lg text-[11.5px]"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text)' }}>
            <Info size={14} strokeWidth={2} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
            <span>
              {t('ak_sd_the_coefficient_is_applied')} <b>{t('ak_sd_exactly_once')}</b> {t('ak_sd_and_only_because_there_is_an_event_in')}
            </span>
          </div>
          <div className="mt-3">
            <SectionTitle>{t('ak_sd_rated_parameters')}</SectionTitle>
            <div className="flex flex-col gap-1.5 text-[12px]">
              {[
                [t('ak_sd_usable_bess_capacity'), `${TECH_STATE.bessUsefulMwh} ${t('ak_sd_mwh')}`],
                [t('ak_sd_charge_power'), t('ak_sd_mw', { n: TECH_STATE.bessChargeMw })],
                [t('ak_sd_discharge_power'), t('ak_sd_mw', { n: TECH_STATE.bessDischargeMw })],
                [t('ak_sd_cycle_efficiency'), String(TECH_STATE.bessRoundTrip)],
                [t('ak_sd_delivery_ceiling'), t('ak_sd_mw', { n: TECH_STATE.gridLimitMw })],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-1" style={{ borderBottom: '1px solid var(--line)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                  <span className="font-semibold" style={{ color: 'var(--text)' }}>{v}</span>
                </div>
              ))}
            </div>
            <div className="text-[10.5px] mt-2" style={{ color: 'var(--text-dim)' }}>
              {t('ak_sd_changed_by_the_technical_dispatcher_ev')}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
